import React, { FC } from "react";
import Box from "@mui/material/Box";
import parse, { domToReact, HTMLReactParserOptions } from "html-react-parser";
import Typography from "@mui/material/Typography";
import {
  messageContainer,
  messageDesign,
} from "../../../styles/ChatBot/CustomMessages.style";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export type TMessage = {
  text: string;
  user: "me" | "other";
  data?: {
    type: "categorySelection" | "siteSelection" | "parkingSelection";
    categories?: any[];
    sites?: any[];
    parkings?: any[];
    onSelect?: (item: any) => void;
  };
};

interface ICustomMessages {
  index?: number;
  message: TMessage;
  selectedId?: string; // Add prop to track selected item
}

const markdownToHtml = (text: string): string => {
  if (!text) return "";
  let html = text;
  // Replace bold: **text** -> <strong>text</strong>
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  // Replace links: [text](url) -> <a href="$2">$1</a>
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  return html;
};

const isSameHostOrEffia = (urlStr: string): boolean => {
  try {
    const url = new URL(urlStr);
    const host = url.hostname.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();
    return (
      host === currentHost ||
      host === "effia.be" ||
      host.endsWith(".effia.be")
    );
  } catch (e) {
    return urlStr.startsWith("/");
  }
};

const getRelativePath = (urlStr: string): string => {
  try {
    const url = new URL(urlStr);
    return url.pathname + url.search + url.hash;
  } catch (e) {
    return urlStr;
  }
};

export const CustomMessages: FC<ICustomMessages> = ({ index = 0, message, selectedId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const renderContent = (rawText: string) => {
    const htmlText = markdownToHtml(rawText);
    const options: HTMLReactParserOptions = {
      replace: (domNode: any) => {
        if (domNode.name === "a") {
          const { href } = domNode.attribs || {};
          const children = domNode.children;
          if (href) {
            if (isSameHostOrEffia(href)) {
              const relativePath = getRelativePath(href);
              return (
                <span
                  style={{
                    color: message.user === "me" ? "inherit" : "#1B374C",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(relativePath);
                  }}
                >
                  {domToReact(children, options)}
                </span>
              );
            } else {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: message.user === "me" ? "inherit" : "#1B374C",
                    textDecoration: "underline",
                    fontWeight: "600",
                  }}
                >
                  {domToReact(children, options)}
                </a>
              );
            }
          }
        }
      },
    };
    return parse(htmlText, options);
  };

  // Helper function to get localized name
  const getLocalizedName = (nameObject: Record<string, string>): string => {
    const currentLang = i18n.language.toUpperCase();
    return (
      nameObject[currentLang] ||
      nameObject["FR"] ||
      Object.values(nameObject)[0] ||
      "N/A"
    );
  };

  // Common style generator for options
  const getOptionStyle = (isSelected: boolean) => ({
    px: "14px",
    py: "8px",
    border: "1px solid",
    borderColor: isSelected ? "#1B374C" : "#9e9e9e", // Gray default, Blue selected
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: isSelected ? "#1B374C" : "white", // Dark blue if selected
    color: isSelected ? "white" : "#757575", // White if selected, Gray default
    fontSize: "13px",
    fontWeight: "500",
    "&:hover": {
      borderColor: "#1B374C", // Blue on hover
      color: isSelected ? "white" : "#1B374C", // Blue text on hover (if not selected)
      backgroundColor: isSelected ? "#152b3c" : "#f5f5f5", // Slightly darker if selected, light gray otherwise
    },
  });

  return (
    <Box
      key={index}
      sx={{
        justifyContent: message.user === "me" ? "flex-end" : "flex-start",
        ...messageContainer,
      }}
    >
      <>
        <Typography
          sx={{
            backgroundColor: message.user === "me" ? "#1B374C" : "#f1f1f1",
            color: message.user === "me" ? "white" : "black",
            ...messageDesign,
          }}
        >
          {message.user === "me" ? renderContent(message.text) : renderContent(t(message.text))}
        </Typography>

        {/* Render selection boxes if data is provided */}
        {message.data && message.user === "other" && (
          <Box sx={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {message.data.type === "categorySelection" &&
              message.data.categories?.map((category) => (
                <Box
                  key={category.id}
                  onClick={() => message.data?.onSelect?.(category)}
                  sx={getOptionStyle(selectedId === category.id)}
                >
                  {getLocalizedName(category.name)}
                </Box>
              ))}

            {message.data.type === "siteSelection" &&
              message.data.sites?.map((site) => (
                <Box
                  key={site.id}
                  onClick={() => message.data?.onSelect?.(site)}
                  sx={getOptionStyle(selectedId === site.id)}
                >
                  {getLocalizedName(site.siteName)}
                </Box>
              ))}

            {message.data.type === "parkingSelection" &&
              message.data.parkings?.map((parking) => (
                <Box
                  key={parking.id}
                  onClick={() => message.data?.onSelect?.(parking)}
                  sx={getOptionStyle(selectedId === parking.id)}
                >
                  {getLocalizedName(parking.parkingName)}
                </Box>
              ))}
          </Box>
        )}
      </>
    </Box>
  );
};
